import React, { useState } from 'react'
import { Accordion, Button, Col, Modal, Row, Container } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { EventDetailList } from './EventDetailTable'
// @ts-ignore
import ModalImage from 'react-modal-image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation, useNavigate } from 'react-router-dom'
import { CoverPhoto, IEvent } from '../../store/events/interfaces'
import { isMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { stages } from '../../routes'
import { RegisterButton } from '../shared/RegisterButton'
import truncate from 'html-truncate';

export const EventModal = (props: {
  eventId: number
  events: IEvent[]
  editable: boolean
}) => {
  const { eventId, events, editable } = props
  const navigate = useNavigate()

  const location = useLocation()
  const parentStage = stages.find(stage =>
    location.pathname.includes(stage.path),
  )

  const [open, setOpen] = useState(false)

  const { data: program } = useSelector((state: RootState) => state.program)

  const isMobile = useMediaQuery(isMobileQuery)

  const eventIdx = events.findIndex(event => event.event_id === eventId)
  const event = events[eventIdx]

  const [showFullDescription, setShowFullDescription] = useState(false);
  const descriptionCharactersLimit = 300;

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const getTruncatedDescription = (html: string, limit: number) => {
    return truncate(html, limit, { ellipsis: '...', slop: 20 });
  };

  if (event === undefined) {
    return <></>
  } else {
    const descriptionToShow = showFullDescription
      ? event?.description_html
      : getTruncatedDescription(event.description_html, descriptionCharactersLimit);
    const nextItem =
      eventIdx === events.length - 1 ? undefined : events[eventIdx + 1]
    const prevItem = eventIdx === 0 ? undefined : events[eventIdx - 1]

    return (
      <>
        <Modal
          size='xl'
          dialogClassName={!isMobile ? 'modal-90w' : ''}
          show={true}
          animation={false}
          onHide={
            () => {
              navigate(
                `${parentStage?.path}`, { replace: true },
              )
            }
          }
        >
          <Modal.Header closeButton closeLabel={'Modal schließen'}>
            <Modal.Title className='w-100'>
              <strong>{event.name}</strong>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Teaser, Description */}
            <Row className='border-bottom mb-3'>
              <Col md={event.cover_photo && program.enable_preview_images ? 6 : 12}>
                {event.teaser ? <p className={"lead"}>{event.teaser}</p> : <></>}
                <div className="float-left mr-2"
                     dangerouslySetInnerHTML={{
                       __html: descriptionToShow,
                     }}
                />
                {!showFullDescription && event.description_html.length > descriptionCharactersLimit && (
                  <Button className="mb-2" variant="secondary" onClick={toggleDescription}>
                    Weiterlesen
                  </Button>
                )}
              </Col>
              {event.cover_photo && program.enable_preview_images ? (
                <Col md={6}>
                  <div className="event-detail-cover-image-wrapper float-right">
                    <img className="event-detail-cover-image"
                      src={event.cover_photo.medium} alt={
                        event.cover_photo.caption
                          ? event.cover_photo.caption
                          : `Vorschaubild der Veranstaltung ${event.name}`
                      } />
                  </div>
                </Col>
              ) : (
                <></>
              )}
            </Row>
            {/* Event Details */}
            <Row>
              <Col md={12}>
                <EventDetailList event={event} />
              </Col>
            </Row>
            {/* Images */}
            {event.photos.length > 0 && (
                <Accordion>
                  <Row>
                    <Col xs={12}>
                      <Accordion.Toggle
                        as={Button}
                        variant='button'
                        eventKey='0'
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        className='btn-info my-2'
                      >
                        <>
                        Bildergalerie öffnen <FontAwesomeIcon icon={open ? 'caret-down' : 'caret-right'} />
                        </>
                      </Accordion.Toggle>
                    </Col>
                  </Row>
                  <Accordion.Collapse eventKey="0">
                    <Container fluid>
                      <Row className='justify-content-between'>
                        {event.photos.map((photo: CoverPhoto, key) => (
                          <Col
                            className="px-0 mx-1 border rounded d-flex flex-column align-items-left"
                            key={
                              event.event_id +
                              `${photo.thumbnail &&
                              photo.thumbnail.split('/').pop()}`
                            }
                          >
                            <ModalImage
                              className='bg-white rounded img-fluid card-img-top modal-image-fix'
                              small={photo.thumbnail}
                              large={photo.medium}
                              alt={
                                photo.caption
                                  ? photo.caption
                                  : `Bild ${key + 1} der Veranstaltung ${event.name}`
                              }
                            />
                            {photo.caption || photo.credits || photo.source ? <div className='pt-1 text-center'><hr className="m-0"/>
                              {photo.caption ? <p className='text-dark px-1'>{photo.caption}</p> : <></>}
                              {photo.credits ? <p className="small m-0 p-0 mb-0 px-1">Urheber: {photo.credits}</p> : <></>}
                              {photo.source ? <p className="small text-muted px-1"><a href={photo.source} target={'_blank'}>Quelle</a></p> : <></>}
                            </div> : <></>}
                          </Col>
                        ))}
                      </Row>
                    </Container>
                  </Accordion.Collapse>
                </Accordion>
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className='w-100'>
              <Row>
                <Col md={6} sm={12} className='d-block d-md-flex'>
                  {prevItem !== undefined && parentStage && (
                    <Button
                      variant='outline-dark'
                      onClick={() => {
                        navigate(
                          `${parentStage.path}/${prevItem.event_id}`, { replace: true },
                        )
                      }}
                      className='mobile-btn'
                    >
                      <FontAwesomeIcon
                        icon='chevron-left'
                        role='presentation'
                      />{' '}
                      vorherige Veranstaltung
                    </Button>
                  )}
                  {nextItem !== undefined && parentStage && (
                    <Button
                      variant='outline-dark'
                      className='ml-0 ml-md-2 mobile-btn'
                      onClick={() => {
                        navigate(
                          `${parentStage.path}/${nextItem.event_id}`, { replace: true },
                        )
                      }}
                    >
                      Nächste Veranstaltung{' '}
                      <FontAwesomeIcon
                        icon='chevron-right'
                        role='presentation'
                      />
                    </Button>
                  )}
                </Col>
                <Col
                  md={6}
                  sm={12}
                  className='d-block d-md-flex justify-content-end'
                >
                  <RegisterButton editable={editable ?? true} event={event} />
                  <Button
                    variant='secondary'
                    onClick={() => navigate(
                      `${parentStage?.path}`, { replace: true },
                    )}
                    className='mobile-btn ml-0 ml-md-2'
                  >
                    Schließen
                  </Button>
                </Col>
              </Row>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    )
  }
}
